import localStorageKeys from "@constants/localStorageKeys";
import userRoles from "@constants/userRoles";

import { UserRole } from "types/entities";
import { OAuthRes } from "types/responses";

export interface AuthService {
  setTokens: (newData: OAuthRes) => void;
  setDefaultRole: (role: string) => void;
  removeTokens: () => void;
  removeRole: () => void;
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  getDefaultRole: () => UserRole;
  isAdministrator: () => boolean;
  isInstructor: () => boolean;
  isLearner: () => boolean;
}

export function createAuthService(storage: Storage = window.localStorage): AuthService {
  const setTokens = (newData: OAuthRes): void => {
    const { access_token, refresh_token } = newData;
    storage.setItem(localStorageKeys.ACCESS_TOKEN, access_token);
    storage.setItem(localStorageKeys.REFRESH_TOKEN, refresh_token);
  };

  const setDefaultRole = (role: string): void => {
    storage.setItem(localStorageKeys.DEFAULT_ROLE, role);
  };

  const removeTokens = (): void => {
    storage.removeItem(localStorageKeys.ACCESS_TOKEN);
    storage.removeItem(localStorageKeys.REFRESH_TOKEN);
  };

  const removeRole = (): void => {
    storage.removeItem(localStorageKeys.DEFAULT_ROLE);
  };

  const getAccessToken = (): string | null => {
    return storage.getItem(localStorageKeys.ACCESS_TOKEN);
  };

  const getRefreshToken = (): string | null => {
    return storage.getItem(localStorageKeys.REFRESH_TOKEN);
  };

  const getDefaultRole = (): UserRole => {
    const storedRole = storage.getItem(localStorageKeys.DEFAULT_ROLE);
    return (storedRole ?? userRoles.LEARNER) as UserRole;
  };

  const isAdministrator = (): boolean => {
    return getDefaultRole() === userRoles.ADMINISTRATOR;
  };

  const isInstructor = (): boolean => {
    return getDefaultRole() === userRoles.INSTRUCTOR;
  };

  const isLearner = (): boolean => {
    return getDefaultRole() === userRoles.LEARNER;
  };

  return {
    setTokens,
    setDefaultRole,
    removeTokens,
    removeRole,
    getAccessToken,
    getRefreshToken,
    getDefaultRole,
    isAdministrator,
    isInstructor,
    isLearner,
  };
}

const authService: AuthService = createAuthService();
export default authService;
